import { FC, useState, Fragment, useEffect, SetStateAction } from 'react';
import styled from '@emotion/styled';
import { DateTime } from 'luxon';
import {
  FormGroup,
  Select as _Select,
  Typography,
  Button,
  Backdrop,
  CircularProgress,
  Dialog,
  IconButton,
  Tooltip,
  Slide,
  Card,
  CardContent,
  Switch,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField as _TextField,
} from '@material-ui/core';
import theme from '../../../../theme';
import { COLOR_TEXT_MAIN } from '../../../../theme/colors';
import { flexibleLayoutContainer } from '../../../styleUtility/flexibleLayout';
import { SPACING_REGULAR } from '../../../constant/spacing';
import Spinner from '../../primitive/Spinner';
import { DatePicker } from '@material-ui/pickers';
import Goal from '../../../../entity/Goal';
import GoalSelector from '../GoalSelector';
import GlobalPeriod from '../../../../entity/GlobalPeriod';
import SettingsIcon from '@material-ui/icons/Settings';
import CloseIcon from '@material-ui/icons/Close';
import { TransitionProps } from '@material-ui/core/transitions/transition';
import React from 'react';
import HelpTip from '../ChartBase/HelpTip';
import LiftReport from '../../../../entity/LiftReport';
import {
  GetGoalsDocument,
  GetGoalsQueryVariables,
  GetGoalsQueryResult,
  CreateLiftReportDocument,
  RemoveLiftReportDocument,
} from '../../../../generated/graphql';
import { useQuery, useMutation } from '@apollo/client';
import mutationVariables from '../../../../utility/mutationVariables';
import MessageDispatcher from '../../../../service/MessageDispatcher';
import { StyledPaper } from '../../primitive/Paper';

type Props = {
  organizationId?: string | null;
  onSelectFromPeriod: (date: DateTime | null) => void;
  onSelectToPeriod: (date: DateTime | null) => void;
  liftReports: LiftReport[] | null;
  goals?: Goal[] | null;
  selectedGoalId: string | null;
  loadingGoals: boolean;
  onSelectGoal: (goal: string | null) => void;
  liftReportChartPeriod: GlobalPeriod;
  liftReportDefaultFrom?: DateTime;
  liftReportDefaultTo?: DateTime;
  updateLiftReportPeriod: (
    setDisabled: (value: SetStateAction<boolean>) => void,
    setLoadingUpdate: (value: SetStateAction<boolean>) => void,
  ) => Promise<void>;
  createLiftReport: (goalId: string, mainGoal: boolean) => Promise<void>;
  removeLiftReport: (goalId: string) => Promise<void>;
  messageDispatcher: MessageDispatcher;
  className?: string;
};

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children?: React.ReactElement },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const LiftReportToolBar: FC<Props> = ({
  organizationId,
  onSelectFromPeriod,
  onSelectToPeriod,
  liftReports,
  // goals,
  selectedGoalId,
  loadingGoals,
  onSelectGoal,
  liftReportChartPeriod,
  liftReportDefaultFrom,
  liftReportDefaultTo,
  updateLiftReportPeriod,
  // createLiftReport,
  // removeLiftReport,
  messageDispatcher,
  className,
}) => {
  const [from, setFrom] = useState<DateTime | null>(liftReportChartPeriod.from);
  const [to, setTo] = useState<DateTime | null>(liftReportChartPeriod.to);
  const [disabled, setDisabled] = useState<boolean>(true);
  const [loadingUpdate, setLoadingUpdate] = useState<boolean>(false);
  const [isOpenLiftReportSettings, setIsOpenLiftReportSettings] = useState<boolean>(false);
  const [currentLiftReports, setCurrentLiftReports] = useState<LiftReport[]>();
  const [switchState, setSwitchState] = useState<boolean[] | undefined>();

  const {
    data: goalData,
    loading: loadingGoalFetch,
    refetch: refetchGoals,
  } = useQuery<GetGoalsQueryResult, GetGoalsQueryVariables>(GetGoalsDocument, {
    skip: !organizationId,
    variables: {
      organizationId: organizationId ? organizationId : '',
    },
    context: {
      clientName: 'liftreport',
    },
    onError() {
      setLoadingUpdate(false);
    },
  });

  /* eslint no-empty-pattern: 0 */
  const [runCreateLiftReportQuery, {}] = useMutation(CreateLiftReportDocument, {
    context: {
      clientName: 'liftReport',
    },
    async onCompleted(data) {
      messageDispatcher.notifyUnimportant('#' + data.createLiftReport.goal.id + 'を集計対象ゴールに追加しました');
      await refetchGoals();
    },
    onError() {
      messageDispatcher.notifyUnimportant('集計対象ゴール追加に失敗しました');
    },
  });

  /* eslint no-empty-pattern: 0 */
  const [runDeleteLiftReportQuery, {}] = useMutation(RemoveLiftReportDocument, {
    context: {
      clientName: 'liftReport',
    },
    async onCompleted() {
      messageDispatcher.notifyUnimportant('集計対象ゴールから削除しました');
      await refetchGoals();
    },
    onError() {
      messageDispatcher.notifyUnimportant('集計対象ゴールの削除に失敗しました');
    },
  });

  useEffect(() => {
    setDisabled(
      liftReportDefaultFrom && liftReportDefaultTo ? (from ? liftReportDefaultFrom.equals(from) : true) : false,
    );
  }, [from]);

  useEffect(() => {
    setDisabled(liftReportDefaultFrom && liftReportDefaultTo ? (to ? liftReportDefaultTo.equals(to) : true) : false);
  }, [to]);

  useEffect(() => {
    goalData && (goalData as any).goals
      ? setDisabled(
          liftReportDefaultFrom && liftReportDefaultTo
            ? from
              ? liftReportDefaultFrom.equals(from)
              : true
            : false || (liftReportDefaultFrom && liftReportDefaultTo)
            ? to
              ? liftReportDefaultTo.equals(to)
              : true
            : false,
        )
      : setDisabled(true);
  }, [selectedGoalId]);

  if (!currentLiftReports && liftReports) {
    setCurrentLiftReports(liftReports);
  }

  if (from !== liftReportChartPeriod.from) {
    setFrom(liftReportChartPeriod.from);
  }

  if (to !== liftReportChartPeriod.to) {
    setTo(liftReportChartPeriod.to);
  }

  useEffect(() => {
    if (goalData && (goalData as any).goals) {
      setSwitchState(((goalData as any).goals as Goal[]).map((goal) => !!goal.liftReport));
    }
  }, [goalData]);

  const handleFromChange = (date: DateTime | null) => {
    setFrom(date);
    date ? onSelectFromPeriod(date) : null;
  };

  const handleToChange = (date: DateTime | null) => {
    setTo(date);
    date ? onSelectToPeriod(date) : null;
  };

  const onClickSaveButton = () => {
    setLoadingUpdate(true);
    updateLiftReportPeriod(setDisabled, setLoadingUpdate);
  };

  const onClickLiftReportSettingButton = () => {
    setIsOpenLiftReportSettings(true);
  };

  const onClickLiftReportSettingClose = () => {
    setIsOpenLiftReportSettings(false);
  };

  const onChangeSwitch = async (goalId: string, mainGoal: boolean, value: boolean, selectedIndex: number) => {
    if (switchState) {
      setSwitchState(switchState.slice().map((state, index) => (index === selectedIndex ? !state : state)));
    } else {
      return;
    }

    if (value) {
      await runCreateLiftReportQuery(
        mutationVariables({
          goalId: goalId,
          mainGoal: mainGoal,
        }),
      );
    } else {
      await runDeleteLiftReportQuery(
        mutationVariables({
          goalId: goalId,
        }),
      );
    }
  };

  // TODO: デフォルト表示のAPIが出来次第つなぎこみ
  // const onChangeRadio = async (goalId: string, mainGoal: boolean) => {
  //   setLoadingUpdate(true);
  //   await runCreateLiftReportQuery(
  //     mutationVariables({
  //       goalId: goalId,
  //       mainGoal: mainGoal,
  //     }),
  //   );
  // };

  return (
    <div
      style={{
        marginBottom: 24,
        position: 'sticky',
        top: 64,
        zIndex: 800,
        marginTop: -24,
      }}
    >
      <StyledPaper className={className}>
        <Dialog open={loadingUpdate}>
          <LiftReportBackDrop open={loadingUpdate}>
            <CircularProgress color="inherit" />
          </LiftReportBackDrop>
        </Dialog>
        <Dialog fullScreen open={isOpenLiftReportSettings} TransitionComponent={Transition}>
          <SettingHeader>
            <IconButton aria-label="close-liftreport-settings" onClick={onClickLiftReportSettingClose}>
              <CloseIcon />
            </IconButton>
          </SettingHeader>
          <SettingBody>
            <Card>
              <CardContent>
                <SettingTitle variant="h4">レポート対象ゴールの管理</SettingTitle>
                <SettingDescription variant="body1">
                  レポートを作成したいゴールを指定してください。「デフォルト表示」にチェックを入れると、組織を選んだときにデフォルトで表示されるようになります。
                </SettingDescription>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>ID</TableCell>
                      <TableCell>ゴール名</TableCell>
                      <TableCell>
                        <TableCellWithHelpTip>
                          レポート作成対象
                          <HelpTip text="有効にするとレポートが作成されます" />
                        </TableCellWithHelpTip>
                      </TableCell>
                      {/* TODO: デフォルト表示のAPIが出来次第つなぎこみ */}
                      {/* <TableCell>
                      <TableCellWithHelpTip>
                        デフォルト表示
                        <HelpTip text="有効にすると組織を選んだときにデフォルトで表示されます" />
                      </TableCellWithHelpTip>
                    </TableCell> */}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loadingGoalFetch ? (
                      <Spinner />
                    ) : goalData && (goalData as any).goals ? (
                      ((goalData as any).goals as Goal[])
                        .slice()
                        .sort((a, b) => {
                          if (parseInt(a.id) < parseInt(b.id)) return -1;
                          if (parseInt(a.id) < parseInt(b.id)) return 1;
                          return 0;
                        })
                        .map((goal, index) => {
                          const currentLiftReport = liftReports?.find((liftReport) => liftReport.goal.id === goal.id);
                          return (
                            <TableRow key={goal.id}>
                              <TableCell>#{goal.id}</TableCell>
                              <TableCell>{goal.title}</TableCell>
                              <TableCell>
                                <Switch
                                  // checked={goal.liftReport ? true : false}
                                  checked={switchState ? switchState[index] : false}
                                  onChange={(event) =>
                                    onChangeSwitch(
                                      goal.id,
                                      currentLiftReport ? currentLiftReport.mainGoal : false,
                                      event.target.checked,
                                      index,
                                    )
                                  }
                                />
                              </TableCell>
                              {/* TODO: デフォルト表示のAPIが出来次第つなぎこみ */}
                              {/* <TableCell>
                                    <Radio
                                      name="main-goal"
                                      checked={currentLiftReport ? currentLiftReport.mainGoal : false}
                                      disabled={goal.liftReport ? false : true}
                                      onChange={event => onChangeRadio(goal.id, event.target.checked)}
                                    />
                                  </TableCell> */}
                            </TableRow>
                          );
                        })
                    ) : null}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </SettingBody>
        </Dialog>
        <div style={{ width: '100%' }}>
          <Form row style={{ width: '100%' }}>
            <GoalContainer>
              {loadingGoals || loadingGoalFetch ? (
                <PeriodSpinner size={24} width={'auto'} />
              ) : goalData && (goalData as any).goals ? (
                <GoalSelector
                  selectedGoalId={selectedGoalId}
                  goals={(goalData as any).goals as Goal[]}
                  onChange={onSelectGoal}
                />
              ) : null}
              {/* {loadingGoals ? null : goals && goals.length > 0 ? (
              <Tooltip title="レポートを作成するゴールを追加・削除できます" placement="bottom">
                <IconButton aria-label="setting" onClick={onClickLiftReportSettingButton}>
                  <SettingsIcon />
                </IconButton>
              </Tooltip>
            ) : null} */}
              {loadingGoals ? null : (
                <Tooltip title="レポートを作成するゴールを追加・削除できます" placement="bottom">
                  <IconButton aria-label="setting" onClick={onClickLiftReportSettingButton}>
                    <SettingsIcon />
                  </IconButton>
                </Tooltip>
              )}
            </GoalContainer>
            <Period>
              {loadingGoals ? (
                <PeriodSpinner size={24} width={'auto'} height={'38px'} />
              ) : (
                <Fragment>
                  <PeriodContainer>
                    <DatePicker
                      inputFormat="yyyy/MM/dd"
                      value={from}
                      //@ts-ignore
                      onChange={handleFromChange}
                      OpenPickerButtonProps={{
                        'aria-label': 'change date',
                      }}
                      maxDate={to}
                      renderInput={(props) => <TextField variant="outlined" {...props} />}
                    />
                    <Typography style={{ margin: '0px 12px' }}>~</Typography>
                    <DatePicker
                      inputFormat="yyyy/MM/dd"
                      value={to}
                      //@ts-ignore
                      onChange={handleToChange}
                      OpenPickerButtonProps={{
                        'aria-label': 'change date',
                      }}
                      minDate={from}
                      renderInput={(props) => <TextField variant="outlined" {...props} />}
                    />
                  </PeriodContainer>
                  <SaveButton onClick={onClickSaveButton} disabled={disabled}>
                    期間を保存
                  </SaveButton>
                </Fragment>
              )}
            </Period>
          </Form>
        </div>
      </StyledPaper>
    </div>
  );
};

// const Root = styled(AppBar)({
//   paddingLeft: SPACING_UNIT,
//   paddingRight: 42,
//   backgroundColor: theme.palette.common.white,
//   borderBottom: `1px solid ${theme.palette.grey['200']}`,
//   boxShadow: 'none',
// });

const Period = styled('div')(
  flexibleLayoutContainer({
    alignItems: 'center',
  }),
  { height: 64, width: '50%' },
);

const GoalContainer = styled('div')(
  flexibleLayoutContainer({
    alignItems: 'center',
  }),
  { height: 64, width: '50%' },
);

const SettingHeader = styled('div')({
  padding: SPACING_REGULAR,
  textAlign: 'right',
});

const SettingBody = styled('div')({
  padding: '0 32px',
});

const SettingTitle = styled(Typography)({
  marginBottom: SPACING_REGULAR,
});

const SettingDescription = styled(Typography)({
  marginBottom: SPACING_REGULAR,
});

const TableCellWithHelpTip = styled('div')({
  display: 'flex',
  alignItems: 'center',
});

const Form = styled(FormGroup)(
  flexibleLayoutContainer({
    justifyContent: 'space-between',
  }),
  {
    width: '100%',
  },
);

const SaveButton = styled(Button)(`
  color: #FFFFFF;
  margin-left: 12px;
  height: 48px;
  font-size: 12px;
  background: ${COLOR_TEXT_MAIN};
  font-weight: 800;
  border-radius: 4px;
  padding: 10px 12px;
  min-width: 86px;
  :hover {
    background: #707889;
    border-color: #707889;
  }
  :disabled {
    background: #E6E7E8;
    color: rgba(75, 85, 106, 0.24);
    border: rgba(75, 85, 106, 0.24);
  }
`);

const PeriodSpinner = styled(Spinner)({
  height: 64,
  marginLeft: SPACING_REGULAR,
  marginRight: SPACING_REGULAR,
  color: theme.palette.common.white,
});

// const SwitchSpinner = styled(Spinner)({
//   float: 'left',
//   paddingLeft: '16px',
// });

const PeriodContainer = styled('div')({
  display: 'flex',
  alignItems: 'center',
});

const TextField = styled(_TextField)({
  // margin: `0px ${SPACING_REGULAR}px`,
  height: 48,
  minHeight: '35px',
  borderColor: '#E6E7E8',
  '& .MuiOutlinedInput-input': {
    padding: '16px 32px 16px 12px',
  },
});

const LiftReportBackDrop = styled(Backdrop)({
  zIndex: theme.zIndex.drawer + 1,
  color: '#fff',
});

export default LiftReportToolBar;
